import { Icon } from '@cbhq/cds-web/icons/Icon';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextHeadline, TextLabel1 } from '@cbhq/cds-web/typography';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { memo, useCallback } from 'react';
import { DisplayType, format } from '@/utils/numberFormatter';
import { PoolsListTokensMedia } from '@/components/Pools/PoolsListTokensMedia';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';
import { useDerivedPositionData } from './useDerivedPositionData';
import type { PositionDetails } from '@/onchain/hooks/usePositions';

type PortfolioPositionRowProps = {
  position: PositionDetails;
  testId?: string;
  onSelect: (positionId: bigint, positionManagerAddress?: `0x${string}`) => void;
};

/**
 * PortfolioPositionMobileRow - Mobile view for a single portfolio position
 *
 * Displays position information in a compact ListCell format optimized for mobile screens.
 * Shows token pair, fee tier, USDC rewards badge (if applicable), liquidity value, and range status.
 */
export const PortfolioPositionMobileRow = memo(function PortfolioPositionMobileRow({
  position,
  testId,
  onSelect,
}: PortfolioPositionRowProps) {
  const { positionTokens, tickInRange, feePercent, totalLiquidityNotional } = position;
  const { baseToken, quoteToken, tokenPair } = useDerivedPositionData(position);
  const hasUSDC = positionTokens.some((token) => token.symbol === 'USDC');

  const handlePress = useCallback(() => {
    onSelect(position.positionId, position.positionManagerAddress);
  }, [onSelect, position.positionId, position.positionManagerAddress]);

  return (
    <ListCell
      testID={testId}
      justifyContent="space-between"
      onClick={handlePress}
      media={<PoolsListTokensMedia size="xl" assets={[baseToken.asset, quoteToken.asset]} />}
      title={<TextHeadline as="span">{tokenPair}</TextHeadline>}
      description={
        <HStack gap={0.5}>
          <TextLabel1
            borderRadius={200}
            paddingX={1}
            paddingY={0.5}
            background="bgAlternate"
            as="span"
          >
            {format({ value: feePercent, type: DisplayType.Percentage })}
          </TextLabel1>
          {hasUSDC && <USDCRewardsChip />}
        </HStack>
      }
      detail={
        <VStack alignItems="flex-end" gap={0.5}>
          <TextBody as="span">
            {format({ value: totalLiquidityNotional, type: DisplayType.Fiat })}
          </TextBody>
          <HStack alignItems="center" gap={0.5}>
            <Icon
              name="dot"
              size="xs"
              active
              color={tickInRange ? 'fgPositive' : 'fgNegative'}
              aria-label={tickInRange ? 'Position in range' : 'Position out of range'}
            />
            <TextBody color="fgMuted" as="span">
              {tickInRange ? 'In range' : 'Out of range'}
            </TextBody>
          </HStack>
        </VStack>
      }
    />
  );
});
